/**
 * Passport schemas
 *
 * Validation schemas for Passport hero - the living record backed by IRR.
 * Note: Passport data is primarily read-only as it's a projection of IRR events.
 */

import { z } from 'zod';

// ═══════════════════════════════════════════════════════════════════════
// STAMP SCHEMAS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Stamp type enum
 */
export const stampTypeSchema = z.enum([
  // CheckUp stamps
  'checkup_started',
  'checkup_completed',
  'iqulture_calculated',
  'mesh_score_updated',
  // TelePortal stamps
  'document_uploaded',
  'document_verified',
  'visa_application_submitted',
  'visa_approved',
  'travel_booked',
  'arrival_confirmed',
  // QultureUp stamps
  'world_unlocked',
  'world_completed',
  'mission_completed',
  'knowledge_assessment_passed',
  // Care stamps
  'checkpoint_completed',
  'wellbeing_assessment',
  'support_requested',
  'support_resolved',
  // General stamps
  'journey_started',
  'milestone_reached',
  'badge_earned',
  'level_up',
]);

/**
 * View stamp schema
 */
export const viewStampSchema = z.object({
  stampId: z.uuid(),
  journeyInstanceId: z.uuid(),
});

/**
 * List stamps schema (filters)
 */
export const listStampsSchema = z.object({
  journeyInstanceId: z.uuid(),
  types: z.array(stampTypeSchema).optional(),
  heroFilter: z.enum(['checkup', 'teleportal', 'qultureup', 'care', 'all']).optional(),
  fromDate: z.coerce.date().optional(),
  toDate: z.coerce.date().optional(),
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(20),
});

// ═══════════════════════════════════════════════════════════════════════
// BADGE SCHEMAS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Badge category enum
 */
export const badgeCategorySchema = z.enum([
  'checkup',
  'teleportal',
  'qultureup',
  'care',
  'engagement',
  'milestone',
  'special',
]);

/**
 * View badge schema
 */
export const viewBadgeSchema = z.object({
  badgeId: z.uuid(),
  talentId: z.uuid(),
});

/**
 * List badges schema (filters)
 */
export const listBadgesSchema = z.object({
  talentId: z.uuid(),
  category: badgeCategorySchema.optional(),
  earnedOnly: z.boolean().default(false),
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(50),
});

// ═══════════════════════════════════════════════════════════════════════
// MILES SCHEMAS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Miles history schema (filters)
 */
export const listMilesHistorySchema = z.object({
  talentId: z.uuid(),
  journeyInstanceId: z.uuid().optional(),
  transactionType: z.enum(['earned', 'redeemed', 'bonus', 'all']).optional(),
  fromDate: z.coerce.date().optional(),
  toDate: z.coerce.date().optional(),
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(20),
});

/**
 * Redeem miles schema
 */
export const redeemMilesSchema = z.object({
  talentId: z.uuid(),
  amount: z.number().int().min(1),
  rewardId: z.uuid(),
  note: z.string().max(500).optional(),
});

// ═══════════════════════════════════════════════════════════════════════
// CITIZEN LEVEL SCHEMAS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Citizen level enum.
 *
 * Names MUST match the canonical taxonomy in `@tonnie/heroes`
 * (CITIZEN_LEVEL_NAMES: Newcomer/Explorer/Voyager/Pioneer/Ambassador) and the
 * badge assets in `@tonnie/assets` (level-3-voyager / level-4-pioneer). Kept as
 * z.enum literals (Zod requires a const tuple); do not let these diverge.
 */
export const citizenLevelSchema = z.enum([
  'newcomer', // Level 1: 0-499 miles
  'explorer', // Level 2: 500-1499 miles
  'voyager', // Level 3: 1500-2999 miles
  'pioneer', // Level 4: 3000-4999 miles
  'ambassador', // Level 5: 5000+ miles
]);

/**
 * View citizen level schema
 */
export const viewCitizenLevelSchema = z.object({
  talentId: z.uuid(),
});

// ═══════════════════════════════════════════════════════════════════════
// TIMELINE SCHEMAS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Timeline view schema (filters)
 */
export const viewTimelineSchema = z.object({
  journeyInstanceId: z.uuid(),
  heroFilter: z.enum(['checkup', 'teleportal', 'qultureup', 'care', 'all']).optional(),
  includeStamps: z.boolean().default(true),
  includeMilestones: z.boolean().default(true),
  fromDate: z.coerce.date().optional(),
  toDate: z.coerce.date().optional(),
});

// ═══════════════════════════════════════════════════════════════════════
// TYPE EXPORTS
// ═══════════════════════════════════════════════════════════════════════

export type StampType = z.infer<typeof stampTypeSchema>;
export type ViewStampInput = z.infer<typeof viewStampSchema>;
export type ListStampsInput = z.infer<typeof listStampsSchema>;
export type BadgeCategory = z.infer<typeof badgeCategorySchema>;
export type ViewBadgeInput = z.infer<typeof viewBadgeSchema>;
export type ListBadgesInput = z.infer<typeof listBadgesSchema>;
export type ListMilesHistoryInput = z.infer<typeof listMilesHistorySchema>;
export type RedeemMilesInput = z.infer<typeof redeemMilesSchema>;
export type CitizenLevel = z.infer<typeof citizenLevelSchema>;
export type ViewCitizenLevelInput = z.infer<typeof viewCitizenLevelSchema>;
export type ViewTimelineInput = z.infer<typeof viewTimelineSchema>;
